
import { Invoice, MatchStatus, ReconciliationItem, ReconciliationSettings, Stats } from '../types';

// Extended Interface for Internal Processing
interface ProcessedInvoice extends Invoice {
    normInvoice: string;
    normGstin: string;
    dateObj: Date | null;
    totalCalc: number;
}

// Group Interface
interface InvoiceGroup {
    key: string;
    items: ProcessedInvoice[];
    aggregated: ProcessedInvoice;
}

// Map of Amendment Type -> Original Type
const AMENDMENT_MAP: Record<string, string> = {
    'B2BA': 'B2B',
    'CDNRA': 'CDNR',
    'ECOA': 'ECO',
    'ISDA': 'ISD',
    'IMPGA': 'IMPG',
    'IMPGSEZA': 'IMPGSEZ'
};

// --- HELPER FUNCTIONS ---

const normalizeStr = (str: string, caseSensitive?: boolean) => {
    if (!str) return '';
    const trimmed = str.trim();
    return caseSensitive ? trimmed : trimmed.toUpperCase();
};

const normalizeInvoice = (str: string, removePadding?: boolean) => {
    if (!str) return '';
    let result = str.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    // If removePadding is true, remove leading zeros
    if (removePadding) {
        result = result.replace(/^0+/, '') || '0'; // Keep at least one zero if all zeros
    }

    return result;
};

// Date diff with unit support (DAYS or MONTHS)
const getDateDiff = (
    d1: Date | null,
    d2: Date | null,
    unit: 'DAYS' | 'MONTHS' = 'DAYS'
): number => {
    if (!d1 || !d2) return 999;

    if (unit === 'MONTHS') {
        // Calculate month difference
        const monthsDiff = Math.abs(
            (d2.getFullYear() - d1.getFullYear()) * 12 +
            (d2.getMonth() - d1.getMonth())
        );
        return monthsDiff;
    }

    // Default: DAYS
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Levenshtein for fuzzy
const levenshtein = (a: string, b: string): number => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    const matrix = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j] + 1
                );
            }
        }
    }
    return matrix[b.length][a.length];
};

const calculateSimilarity = (s1: string, s2: string) => {
    const longer = s1.length > s2.length ? s1 : s2;
    if (longer.length === 0) return 1.0;
    return (longer.length - levenshtein(s1, s2)) / longer.length;
};

// --- PRE-PROCESSING & AGGREGATION ---

const applyReplacements = (str: string, settings?: ReconciliationSettings) => {
    if (!settings?.gstinConfig?.replacements) return str;
    let res = str;
    settings.gstinConfig.replacements.forEach(rep => {
        if (rep.from && rep.to) {
            // Global replacement
            res = res.split(rep.from).join(rep.to);
        }
    });
    return res;
};

const preprocess = (item: Invoice, settings?: ReconciliationSettings): ProcessedInvoice => {
    // Respect case sensitivity setting
    let normGstin = normalizeStr(item.gstin, settings?.caseSensitive);

    // Apply GSTIN corrections (O -> 0, etc) BEFORE any grouping/matching
    if (settings) {
        normGstin = applyReplacements(normGstin, settings);
    }

    // Use padding setting: if padding is false, remove leading zeros
    const removePadding = settings?.additionalReconSettings?.invoiceNumber?.padding === false;
    const normInvoice = normalizeInvoice(item.invoiceNumber, removePadding);

    const dateObj = item.invoiceDate ? new Date(item.invoiceDate) : null;
    const igst = item.igst || 0;
    const cgst = item.cgst || 0;
    const sgst = item.sgst || 0;
    const totalCalc = igst + cgst + sgst;

    return {
        ...item,
        normGstin,
        normInvoice,
        dateObj,
        totalCalc
    };
};

const aggregateGroup = (items: ProcessedInvoice[], key: string): ProcessedInvoice => {
    const sortedItems = [...items].sort((a, b) => b.totalAmount - a.totalAmount);
    const head = sortedItems[0];

    const totalTaxable = items.reduce((sum, i) => sum + (i.taxableValue || 0), 0);
    const totalIgst = items.reduce((sum, i) => sum + (i.igst || 0), 0);
    const totalCgst = items.reduce((sum, i) => sum + (i.cgst || 0), 0);
    const totalSgst = items.reduce((sum, i) => sum + (i.sgst || 0), 0);
    const totalVal = items.reduce((sum, i) => sum + (i.totalAmount || 0), 0);
    const totalTax = totalIgst + totalCgst + totalSgst;

    return {
        ...head,
        id: items.length === 1 ? head.id : `AGG_${key}_${Math.random().toString(36).substr(2, 5)}`,
        taxableValue: totalTaxable,
        taxAmount: totalTax,
        igst: totalIgst,
        cgst: totalCgst,
        sgst: totalSgst,
        totalAmount: totalVal,
        totalCalc: totalTax,
        invoiceNumber: head.invoiceNumber,
        gstin: head.gstin,
        invoiceDate: head.invoiceDate,
        dateObj: head.dateObj
    };
};

const groupInvoices = (list: Invoice[], settings?: ReconciliationSettings): Map<string, InvoiceGroup> => {
    const groups = new Map<string, InvoiceGroup>();

    list.forEach(item => {
        const proc = preprocess(item, settings);
        const key = `${proc.normGstin}|${proc.normInvoice}`;

        if (!groups.has(key)) {
            groups.set(key, { key, items: [], aggregated: proc });
        }
        groups.get(key)!.items.push(proc);
    });

    groups.forEach((group) => {
        if (group.items.length > 0) {
            group.aggregated = aggregateGroup(group.items, group.key);
        }
    });

    return groups;
};

// --- MATCH LOGIC ---

const checkTaxMatch = (
    diff: number,
    total: number,
    settings: ReconciliationSettings
): string | false => {
    if (diff <= 1.0) return 'ROUNDOFF';

    const toleranceUnit = settings.additionalReconSettings?.taxValue?.toleranceUnit || 'BOTH';

    // Check INR tolerance
    if (toleranceUnit === 'INR' || toleranceUnit === 'BOTH') {
        if (diff <= settings.taxTolerance) return 'INR';
    }

    // Check PERCENT tolerance
    if (toleranceUnit === 'PERCENT' || toleranceUnit === 'BOTH') {
        if (total > 0 && (diff / total) * 100 <= (settings.taxTolerancePercent || 1.0)) {
            return 'PERCENT';
        }
    }

    return false;
};

const checkGstinFuzzy = (g1: string, g2: string, settings: ReconciliationSettings) => {
    if (g1 === g2) return true;
    if (!settings.gstinConfig?.enableFuzzy) return false;

    // Optimization: If length differs significantly, don't compute distance
    if (Math.abs(g1.length - g2.length) > 2) return false;

    const sim = calculateSimilarity(g1, g2);
    return (sim * 100) >= (settings.gstinConfig.similarityThreshold || 85);
};

// Invoice type matching helper
const checkInvoiceTypeMatch = (
    type1: string,
    type2: string,
    mode: 'IGNORE' | 'EXACT' | 'FUZZY' = 'IGNORE'
): boolean => {
    if (mode === 'IGNORE') return true;
    if (mode === 'EXACT') return type1 === type2;

    // FUZZY: Check if types match or if amendment types match their base types
    // e.g., B2BA matches B2B, CDNRA matches CDNR
    if (type1 === type2) return true;

    const baseType1 = AMENDMENT_MAP[type1] || type1;
    const baseType2 = AMENDMENT_MAP[type2] || type2;

    return baseType1 === baseType2;
};

const createResult = (
    prGroup: InvoiceGroup | null,
    gstrGroup: InvoiceGroup | null,
    status: MatchStatus,
    remarks: string,
    details: string,
    diff: number,
    activePeriod: string,
    dateDiff: number = 0
): ReconciliationItem => {
    const matchedMonth = (status === MatchStatus.MATCHED || status === MatchStatus.PARTIALLY_MATCHED) ? activePeriod : undefined;

    const pr = prGroup?.aggregated;
    const gstr = gstrGroup?.aggregated;

    return {
        id: (pr?.id || gstr?.id || Math.random().toString()) + '_res',
        status,
        remarks,
        mismatchDetails: details,
        diffTax: diff,
        dateDiff,

        prInvoiceRef: pr ? { ...pr, matchedMonth } : undefined,
        gstrInvoiceRef: gstr ? { ...gstr, matchedMonth } : undefined,

        prMatches: prGroup?.items,
        gstrMatches: gstrGroup?.items,

        prGstin: pr?.gstin, gstrGstin: gstr?.gstin,
        prPartyName: pr?.vendorName, gstrPartyName: gstr?.vendorName,
        prInvoiceNo: pr?.invoiceNumber, gstrInvoiceNo: gstr?.invoiceNumber,
        prDate: pr?.invoiceDate, gstrDate: gstr?.invoiceDate,

        prIgst: pr?.igst || 0, gstrIgst: gstr?.igst || 0,
        prCgst: pr?.cgst || 0, gstrCgst: gstr?.cgst || 0,
        prSgst: pr?.sgst || 0, gstrSgst: gstr?.sgst || 0,

        uploadedMonth: pr?.uploadedMonth || gstr?.uploadedMonth || activePeriod,
        matchedMonth
    };
};

export const reconcileData = (
    prList: Invoice[],
    gstrList: Invoice[],
    settings: ReconciliationSettings,
    activePeriod?: string,
    clientGstin?: string
): ReconciliationItem[] => {
    const currentMonth = activePeriod || new Date().toISOString().slice(0, 7);
    const results: ReconciliationItem[] = [];

    // --- PASS 0: HANDLE AMENDMENTS ---

    const supersededIds = new Set<string>();
    const amendments = gstrList.filter(i => AMENDMENT_MAP[i.type]);
    const originalCandidates = gstrList.filter(i => Object.values(AMENDMENT_MAP).includes(i.type));

    amendments.forEach(amend => {
        const targetType = AMENDMENT_MAP[amend.type];
        const original = originalCandidates.find(o =>
            o.type === targetType &&
            normalizeStr(o.gstin) === normalizeStr(amend.gstin) &&
            normalizeInvoice(o.invoiceNumber) === normalizeInvoice(amend.invoiceNumber)
        );

        if (original) {
            supersededIds.add(original.id);

            // Calculate differences
            const changes: string[] = [];

            // Helper to format date for comparison display
            const fmtDate = (d: string | undefined) => d || 'N/A';

            if (original.invoiceDate !== amend.invoiceDate) {
                changes.push(`Date: ${fmtDate(original.invoiceDate)} -> ${fmtDate(amend.invoiceDate)}`);
            }
            if (original.taxableValue !== amend.taxableValue) {
                changes.push(`Taxable: ${original.taxableValue} -> ${amend.taxableValue}`);
            }
            if (original.igst !== amend.igst) {
                changes.push(`IGST: ${original.igst} -> ${amend.igst}`);
            }
            if (original.cgst !== amend.cgst) {
                changes.push(`CGST: ${original.cgst} -> ${amend.cgst}`);
            }
            if (original.sgst !== amend.sgst) {
                changes.push(`SGST: ${original.sgst} -> ${amend.sgst}`);
            }
            if (original.totalAmount !== amend.totalAmount) {
                changes.push(`Total: ${original.totalAmount} -> ${amend.totalAmount}`);
            }

            const changeText = changes.length > 0 ? ` (${changes.join(', ')})` : ' (No significant value changes)';

            const originalGroup: InvoiceGroup = {
                key: original.id,
                items: [preprocess(original, settings)],
                aggregated: preprocess(original, settings)
            };
            results.push(createResult(
                null, originalGroup, MatchStatus.AMENDMENT,
                `Superseded by ${amend.type}${changeText}`,
                `Superseded by ${amend.invoiceNumber}`,
                0, currentMonth
            ));
        }
    });

    const effectiveGstrList = gstrList.filter(i => !supersededIds.has(i.id));

    // --- PASS 0.1: INTERNAL DUPLICATE DETECTION ---

    const prDuplicateIds = new Set<string>();
    const gstrDuplicateIds = new Set<string>();

    if (settings.duplicateDetection) {
        // --- CATEGORY A & B: PR DUPLICATES ---
        const prSeenExact = new Map<string, Invoice>();
        const prSeenLogical = new Map<string, ProcessedInvoice>();

        prList.forEach(item => {
            const proc = preprocess(item, settings);
            const dateStr = proc.dateObj ? proc.dateObj.getTime() : 'nodate';

            // A. EXACT DUPLICATE KEY
            const exactKey = `${proc.normGstin}|${proc.normInvoice}|${dateStr}|${proc.igst}|${proc.cgst}|${proc.sgst}|${proc.taxableValue}`;

            if (prSeenExact.has(exactKey)) {
                const original = prSeenExact.get(exactKey)!;
                prDuplicateIds.add(item.id);
                results.push(createResult(
                    { key: item.id, items: [proc], aggregated: proc }, null,
                    MatchStatus.DUPLICATE, `Internal Duplicate (Exact)`,
                    `Exactly matches record ${original.invoiceNumber} (ID: ${original.id})`,
                    0, currentMonth
                ));
                return;
            }
            prSeenExact.set(exactKey, item);

            // B. LOGICAL DUPLICATE CHECK (Only for PR)
            // Same GSTIN + Inv No
            const baseKey = `${proc.normGstin}|${proc.normInvoice}`;
            const logicalMatches = Array.from(prSeenLogical.values()).filter(p =>
                p.normGstin === proc.normGstin &&
                p.normInvoice === proc.normInvoice &&
                p.id !== item.id
            );

            for (const match of logicalMatches) {
                const dateDiff = getDateDiff(proc.dateObj, match.dateObj, 'DAYS');
                const taxDiff = Math.abs(proc.totalCalc - match.totalCalc);

                const isDateWithin = dateDiff <= (settings.dateTolerance || 0);
                const isTaxWithin = checkTaxMatch(taxDiff, proc.totalCalc, settings) !== false;

                if (isDateWithin && isTaxWithin) {
                    prDuplicateIds.add(item.id);
                    results.push(createResult(
                        { key: item.id, items: [proc], aggregated: proc }, null,
                        MatchStatus.DUPLICATE, `Logical Duplicate (PR)`,
                        `Similar to record ${match.invoiceNumber} (Date Diff: ${dateDiff}d, Tax Diff: ${taxDiff.toFixed(2)})`,
                        taxDiff, currentMonth, dateDiff
                    ));
                    return;
                }
            }
            prSeenLogical.set(item.id, proc);
        });

        // --- CATEGORY A & C: 2B DUPLICATES ---
        const gstrSeenExact = new Map<string, Invoice>();
        const gstrSeenSystem = new Map<string, ProcessedInvoice>();

        effectiveGstrList.forEach(item => {
            const proc = preprocess(item, settings);
            const dateStr = proc.dateObj ? proc.dateObj.getTime() : 'nodate';

            // A. EXACT DUPLICATE KEY (2B)
            const exactKey = `${proc.normGstin}|${proc.normInvoice}|${dateStr}|${proc.igst}|${proc.cgst}|${proc.sgst}|${proc.taxableValue}`;

            if (gstrSeenExact.has(exactKey)) {
                const original = gstrSeenExact.get(exactKey)!;
                gstrDuplicateIds.add(item.id);
                results.push(createResult(
                    null, { key: item.id, items: [proc], aggregated: proc },
                    MatchStatus.DUPLICATE, `Internal Duplicate (Exact)`,
                    `2B Exactly matches record ${original.invoiceNumber}`,
                    0, currentMonth
                ));
                return;
            }
            gstrSeenExact.set(exactKey, item);

            // C. 2B SYSTEM DUPLICATE (Same GSTIN + Inv + Total Tax)
            const systemKey = `${proc.normGstin}|${proc.normInvoice}|${proc.totalCalc.toFixed(2)}`;
            const systemMatch = Array.from(gstrSeenSystem.values()).find(p =>
                p.normGstin === proc.normGstin &&
                p.normInvoice === proc.normInvoice &&
                Math.abs(p.totalCalc - proc.totalCalc) < 0.01
            );

            if (systemMatch) {
                gstrDuplicateIds.add(item.id);
                results.push(createResult(
                    null, { key: item.id, items: [proc], aggregated: proc },
                    MatchStatus.DUPLICATE, `System Duplicate (2B)`,
                    `Portal redundancy for ${proc.invoiceNumber} with same tax amount`,
                    0, currentMonth
                ));
                return;
            }
            gstrSeenSystem.set(item.id, proc);
        });
    }

    const cleanPrList = prList.filter((i: Invoice) => !prDuplicateIds.has(i.id));
    const cleanGstrList = effectiveGstrList.filter((i: Invoice) => !gstrDuplicateIds.has(i.id));

    // --- RECONCILIATION FLOW ---

    // 1. Group Data (Passing settings ensures GSTIN replacements happen here)
    const prGroups = groupInvoices(cleanPrList, settings);
    const gstrGroups = groupInvoices(cleanGstrList, settings);

    const gstrMap = new Map<string, InvoiceGroup>();
    gstrGroups.forEach(group => {
        const baseKey = `${group.aggregated.normGstin}|${group.aggregated.normInvoice}`;
        gstrMap.set(baseKey, group);
    });

    const matchedGstrKeys = new Set<string>();
    const matchedPrKeys = new Set<string>(); // EXPLICITLY TRACK MATCHED PR GROUPS

    // --- PASS 0.5: INTER-BRANCH DETECTION (PRIORITY) ---
    // Check if client PAN matches both PR and 2B PAN - if so, mark as Inter-Branch IMMEDIATELY
    // This runs BEFORE any other reconciliation to ensure proper classification

    const panMatchEnabled = settings.panMatch !== false;
    const normalizedClientGstin = (clientGstin || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const clientPan = normalizedClientGstin.length >= 15 ? normalizedClientGstin.substring(2, 12) : null;

    // --- PRE-INDEX 2B DATA ---
    // Group 2B items by GSTIN and by PAN to allow O(1) or O(K) lookups instead of O(N)
    const gstrByGstin = new Map<string, InvoiceGroup[]>();
    const gstrByPan = new Map<string, InvoiceGroup[]>();

    gstrGroups.forEach(group => {
        const normGstin = group.aggregated.normGstin;
        const pan = normGstin.length >= 12 ? normGstin.substring(2, 12) : 'NOPAN';

        if (!gstrByGstin.has(normGstin)) gstrByGstin.set(normGstin, []);
        gstrByGstin.get(normGstin)!.push(group);

        if (!gstrByPan.has(pan)) gstrByPan.set(pan, []);
        gstrByPan.get(pan)!.push(group);
    });

    if (panMatchEnabled && clientPan) {
        // PRE-FILTER 2B for inter-branch candidates (PAN matches client PAN)
        const interBranch2bCandidates = gstrByPan.get(clientPan) || [];

        // Helper to run inter-branch matching logic
        const runMatchingPass = (strictInvoiceMatch: boolean) => {
            prGroups.forEach(prGroup => {
                if (matchedPrKeys.has(prGroup.key)) return;

                const pr = prGroup.aggregated;
                const normalizedPrGstin = (pr.gstin || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
                if (normalizedPrGstin.length < 12) return;
                const prPan = normalizedPrGstin.substring(2, 12);

                if (prPan !== clientPan) return;

                let bestMatch: any = null;
                let bestScore = Infinity;

                // Optimization: Only loop over pre-filtered 2B inter-branch candidates
                interBranch2bCandidates.forEach(gstrGroup => {
                    if (matchedGstrKeys.has(gstrGroup.key)) return;

                    const gstr = gstrGroup.aggregated;
                    if (strictInvoiceMatch && pr.normInvoice !== gstr.normInvoice) return;

                    const taxDiff = Math.abs(pr.totalCalc - gstr.totalCalc);
                    const prDate = pr.dateObj ? pr.dateObj.getTime() : 0;
                    const gstrDate = gstr.dateObj ? gstr.dateObj.getTime() : 0;
                    const dateDiffDays = Math.abs(prDate - gstrDate) / (1000 * 60 * 60 * 24);

                    let score = taxDiff + (dateDiffDays * 10);
                    if (pr.normInvoice === gstr.normInvoice) {
                        score -= 1000000;
                    }

                    if (score < bestScore) {
                        bestScore = score;
                        const dateToleranceUnit = settings.additionalReconSettings?.date?.toleranceUnit || 'DAYS';
                        const dateDiff = getDateDiff(pr.dateObj, gstr.dateObj, dateToleranceUnit);
                        bestMatch = { group: gstrGroup, taxDiff, dateDiff };
                    }
                });

                if (bestMatch) {
                    const match = bestMatch!;
                    const gstr = match.group.aggregated;
                    const dateToleranceUnit = settings.additionalReconSettings?.date?.toleranceUnit || 'DAYS';
                    const dateDiff = match.dateDiff;
                    const dateTolerance = dateToleranceUnit === 'MONTHS' ? Math.floor(settings.dateTolerance / 30) : settings.dateTolerance;
                    const isInvMatch = pr.normInvoice === gstr.normInvoice;
                    const isDateMatch = dateDiff <= dateTolerance;
                    const isTaxMatch = checkTaxMatch(match.taxDiff, gstr.totalCalc, settings);

                    let matchQuality = isInvMatch && isDateMatch && isTaxMatch ? 'Exact Match' :
                        isDateMatch && isTaxMatch ? 'Matched (Inv No Differs)' :
                            isTaxMatch ? 'Tax Match' : isDateMatch ? 'Date Match' : 'Partial Match';

                    results.push(createResult(prGroup, match.group, MatchStatus.INTER_BRANCH, `Inter-Branch: ${matchQuality} (Client PAN: ${clientPan})`, `Tax Diff: ₹${match.taxDiff.toFixed(2)}, Date Diff: ${dateDiff}${dateToleranceUnit === 'MONTHS' ? 'm' : 'd'}`, match.taxDiff, currentMonth, dateDiff));
                    matchedPrKeys.add(prGroup.key);
                    matchedGstrKeys.add(match.group.key);
                }
            });
        };

        runMatchingPass(true);
        runMatchingPass(false);

        prGroups.forEach(prGroup => {
            if (matchedPrKeys.has(prGroup.key)) return;
            const pr = prGroup.aggregated;
            const normalizedPrGstin = (pr.gstin || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
            if (normalizedPrGstin.length >= 12 && normalizedPrGstin.substring(2, 12) === clientPan) {
                results.push(createResult(prGroup, null, MatchStatus.INTER_BRANCH, `Inter-Branch: No 2B Record Found (Client PAN Match)`, 'Missing in 2B', pr.totalAmount, currentMonth, 0));
                matchedPrKeys.add(prGroup.key);
            }
        });
    }

    // --- PASS 1: DIRECT MATCH (INVOICE NO + GSTIN) ---
    // If GSTIN had typos defined in settings, they are already corrected in `normGstin`
    // so this will catch them as "Exact Matches"

    prGroups.forEach((prGroup, prKey) => {
        // Skip if already matched (e.g., as inter-branch in Pass 0.5)
        if (matchedPrKeys.has(prKey)) return;

        const isHistorical = prGroup.items.some(i => i.matchedMonth && i.matchedMonth < currentMonth);
        if (isHistorical) return;

        const pr = prGroup.aggregated;

        // Skip direct match if critical fields are empty
        if (!pr.normGstin || !pr.normInvoice) {
            return;
        }

        const baseKey = `${pr.normGstin}|${pr.normInvoice}`;
        const gstrGroup = gstrMap.get(baseKey);
        if (gstrGroup) {
            // Skip if this 2B item was already matched (e.g., as inter-branch)
            if (matchedGstrKeys.has(gstrGroup.key)) return;

            matchedGstrKeys.add(gstrGroup.key);
            matchedPrKeys.add(prKey);

            const gstr = gstrGroup.aggregated;
            const diff = Math.abs(pr.totalCalc - gstr.totalCalc);

            // Use date tolerance unit from settings
            const dateToleranceUnit = settings.additionalReconSettings?.date?.toleranceUnit || 'DAYS';
            const dateDiff = getDateDiff(pr.dateObj, gstr.dateObj, dateToleranceUnit);

            const taxMatchType = checkTaxMatch(diff, gstr.totalCalc, settings);

            // Calculate tolerance value based on unit
            const dateTolerance = dateToleranceUnit === 'MONTHS'
                ? Math.floor(settings.dateTolerance / 30) // Convert days to approximate months
                : settings.dateTolerance;
            const isDateMatch = dateDiff <= dateTolerance;
            const isIneligible = gstr.itcAvailability === 'No';

            let matched = false;
            for (const rule of settings.partialMatchRules || []) {
                if (rule.enabled) {
                    const criteria = rule.criteria;
                    const conditionsMet = [
                        !criteria.gstinMatch || (pr.normGstin === gstr.normGstin),
                        !criteria.invoiceNumberMatch || (pr.normInvoice === gstr.normInvoice),
                        !criteria.taxValueMatch || !!taxMatchType,
                        !criteria.invoiceDateMatch || isDateMatch,
                    ];

                    if (conditionsMet.every(c => c)) {
                        results.push(createResult(
                            prGroup, gstrGroup, rule.resultingStatus,
                            rule.resultingRemarks, `Diff: ₹${diff.toFixed(2)}`, diff, currentMonth, dateDiff
                        ));
                        matched = true;
                        break;
                    }
                }
            }

            if (!matched) {
                if (isDateMatch && taxMatchType) {
                    let remark = taxMatchType === 'ROUNDOFF' ? 'Round-off Match' : 'Exact Match';
                    if (isIneligible) remark += " (Ineligible ITC - Do Not Claim)";

                    results.push(createResult(
                        prGroup, gstrGroup, MatchStatus.MATCHED,
                        remark, `Diff: ₹${diff.toFixed(2)}`, diff, currentMonth, dateDiff
                    ));
                } else {
                    let remark = 'Mismatch';
                    if (!taxMatchType) remark = 'Tax Mismatch';
                    if (!isDateMatch) remark = 'Date Mismatch';
                    if (!taxMatchType && !isDateMatch) remark = 'Tax and Date Mismatch';

                    results.push(createResult(
                        prGroup, gstrGroup, MatchStatus.PARTIALLY_MATCHED,
                        remark, `Tax Diff: ₹${diff.toFixed(2)}, Date Diff: ${dateDiff}d`, diff, currentMonth, dateDiff
                    ));
                }
            }
            return;
        }
    });

    // --- PASS 2: FUZZY MATCH (Invoice OR GSTIN) ---
    // Now supports GSTIN fuzzy match via checkGstinFuzzy and Invoice fuzzy via invoiceConfig

    const enableInvoiceFuzzy = settings.invoiceConfig?.enableFuzzy ?? settings.fuzzyMatch;

    if (enableInvoiceFuzzy || settings.gstinConfig?.enableFuzzy) {
        prGroups.forEach((prGroup, prKey) => {
            if (matchedPrKeys.has(prKey)) return;

            const isHistorical = prGroup.items.some(i => i.matchedMonth && i.matchedMonth < currentMonth);
            if (isHistorical) return;

            const prGstin = prGroup.aggregated.normGstin;
            const prInv = prGroup.aggregated.normInvoice;
            const prPan = prGstin.length >= 12 ? prGstin.substring(2, 12) : 'NOPAN';

            // Skip fuzzy match if critical PR fields are empty
            if (!prGstin || !prInv) {
                return;
            }

            let bestMatch: { group: InvoiceGroup, score: number, type: string } | null = null;

            // Optimization: Only check candidates from same GSTIN bucket or same PAN bucket (for fuzzy GSTIN)
            const sameGstinCandidates = gstrByGstin.get(prGstin) || [];
            const samePanCandidates = settings.gstinConfig?.enableFuzzy ? (gstrByPan.get(prPan) || []) : [];

            // deduplicate candidates if both buckets are used
            const candidates = (settings.gstinConfig?.enableFuzzy && sameGstinCandidates !== samePanCandidates)
                ? Array.from(new Set([...sameGstinCandidates, ...samePanCandidates]))
                : sameGstinCandidates;

            for (const gGroup of candidates) {
                if (matchedGstrKeys.has(gGroup.key)) continue;

                const gGstin = gGroup.aggregated.normGstin;
                const gInv = gGroup.aggregated.normInvoice;

                // Skip fuzzy match if critical GSTR fields are empty
                if (!gGstin || !gInv) {
                    continue;
                }

                // 1. Check GSTIN Match (Exact OR Fuzzy)
                const isGstinExact = gGstin === prGstin;
                const isGstinFuzzy = !isGstinExact && checkGstinFuzzy(prGstin, gGstin, settings);

                if (!isGstinExact && !isGstinFuzzy) continue; // No match on GSTIN, skip

                // 2. Check Date Match (Strict tolerance required for fuzzy)
                const dateToleranceUnit = settings.additionalReconSettings?.date?.toleranceUnit || 'DAYS';
                const dDiff = getDateDiff(prGroup.aggregated.dateObj, gGroup.aggregated.dateObj, dateToleranceUnit);
                const dateTolerance = dateToleranceUnit === 'MONTHS'
                    ? Math.floor(settings.dateTolerance / 30)
                    : settings.dateTolerance;
                if (dDiff > dateTolerance) continue;

                // 3. Check Tax Match (Strict tolerance required for fuzzy)
                const fDiff = Math.abs(prGroup.aggregated.totalCalc - gGroup.aggregated.totalCalc);
                const isTaxMatch = checkTaxMatch(fDiff, gGroup.aggregated.totalCalc, settings);
                if (!isTaxMatch) continue;

                // 4. Check Invoice Number Similarity
                const invSim = calculateSimilarity(prInv, gInv);
                const isInvExact = prInv === gInv;

                // Configured Threshold (Normalized to 0-1)
                const invThreshold = (settings.invoiceConfig?.similarityThreshold || 80) / 100;

                // Scoring Logic
                let score = 0;
                let type = '';

                if (isGstinExact && !isInvExact && enableInvoiceFuzzy && invSim >= invThreshold) {
                    score = invSim; // Standard Fuzzy Invoice
                    type = 'Invoice';
                } else if (isGstinFuzzy && isInvExact) {
                    score = 0.95; // Fuzzy GSTIN, Exact Invoice (Very High confidence)
                    type = 'GSTIN';
                } else if (isGstinFuzzy && !isInvExact && enableInvoiceFuzzy && invSim >= invThreshold) {
                    score = invSim * 0.9; // Both Fuzzy (Requires very high invoice sim)
                    type = 'Both';
                }

                if (score > 0) {
                    if (!bestMatch || score > bestMatch.score) {
                        bestMatch = { group: gGroup, score, type };
                    }
                }
            }

            if (bestMatch) {
                const gstrGroup = bestMatch.group;
                matchedGstrKeys.add(gstrGroup.key);
                matchedPrKeys.add(prKey);

                const diff = Math.abs(prGroup.aggregated.totalCalc - gstrGroup.aggregated.totalCalc);
                const dateDiff = getDateDiff(prGroup.aggregated.dateObj, gstrGroup.aggregated.dateObj);
                const isIneligible = gstrGroup.aggregated.itcAvailability === 'No';

                let matchLabel = '';
                if (bestMatch.type === 'Invoice') matchLabel = `Probable Match (Fuzzy Invoice ${(bestMatch.score * 100).toFixed(0)}%)`;
                else if (bestMatch.type === 'GSTIN') matchLabel = `Probable Match (Fuzzy GSTIN)`;
                else matchLabel = `Probable Match (Fuzzy GSTIN & Invoice)`;

                let remark = matchLabel;
                if (isIneligible) remark += " (Ineligible ITC)";

                results.push(createResult(
                    prGroup, gstrGroup, MatchStatus.PARTIALLY_MATCHED,
                    remark, `Diff: ₹${diff.toFixed(2)}`, diff, currentMonth, dateDiff
                ));
            }
        });
    }

    // --- PASS 3: UNMATCHED ITEMS ---

    const unmatchedPrGroups: InvoiceGroup[] = [];
    prGroups.forEach((prGroup, prKey) => {
        if (!matchedPrKeys.has(prKey)) {
            unmatchedPrGroups.push(prGroup);
        }
    });

    const unmatchedGstrGroups: InvoiceGroup[] = [];
    gstrGroups.forEach((gstrGroup, gstrKey) => {
        if (!matchedGstrKeys.has(gstrKey)) {
            unmatchedGstrGroups.push(gstrGroup);
        }
    });

    if (settings.additionalReconSettings?.enabled) {
        const additionalResults = runAdditionalReconciliation(unmatchedPrGroups, unmatchedGstrGroups, settings, currentMonth, clientGstin);
        results.push(...additionalResults);

        // Remove the newly matched items from the unmatched groups
        const additionallyMatchedPrKeys = new Set(additionalResults.map(r => r.prInvoiceRef?.id));
        const additionallyMatchedGstrKeys = new Set(additionalResults.map(r => r.gstrInvoiceRef?.id));

        unmatchedPrGroups.forEach((prGroup, i) => {
            if (additionallyMatchedPrKeys.has(prGroup.aggregated.id)) {
                unmatchedPrGroups.splice(i, 1);
            }
        });
        unmatchedGstrGroups.forEach((gstrGroup, i) => {
            if (additionallyMatchedGstrKeys.has(gstrGroup.aggregated.id)) {
                unmatchedGstrGroups.splice(i, 1);
            }
        });
    }


    // Unmatched PR
    unmatchedPrGroups.forEach((prGroup) => {
        const isHistorical = prGroup.items.some(i => i.matchedMonth && i.matchedMonth < currentMonth);
        if (isHistorical) return;

        const isCarryForward = prGroup.items.some(i => i.uploadedMonth && i.uploadedMonth < currentMonth);

        results.push(createResult(
            prGroup,
            null,
            isCarryForward ? MatchStatus.CARRY_FORWARD : MatchStatus.UNMATCHED_PR,
            isCarryForward ? 'Carry Forward' : 'Not in 2B',
            'No matching invoice found in GSTR-2B',
            prGroup.aggregated.totalCalc,
            currentMonth
        ));
    });

    // Unmatched 2B
    unmatchedGstrGroups.forEach((gstrGroup) => {
        const head = gstrGroup.aggregated;
        if (head.rcm === 'Yes') {
            results.push(createResult(null, gstrGroup, MatchStatus.RCM, 'RCM Supplies', 'Reverse Charge', 0, currentMonth));
            return;
        }
        if (head.itcAvailability === 'No') {
            results.push(createResult(null, gstrGroup, MatchStatus.INELIGIBLE_ITC, 'Ineligible ITC', 'ITC Flagged No', 0, currentMonth));
            return;
        }

        results.push(createResult(
            null,
            gstrGroup,
            MatchStatus.UNMATCHED_2B,
            'Not in PR',
            'Invoice not found in Purchase Register',
            head.totalCalc,
            currentMonth
        ));
    });

    return results;
};

const runAdditionalReconciliation = (
    unmatchedPrGroups: InvoiceGroup[],
    unmatchedGstrGroups: InvoiceGroup[],
    settings: ReconciliationSettings,
    activePeriod: string,
    clientGstin?: string
): ReconciliationItem[] => {
    const results: ReconciliationItem[] = [];
    if (!settings.additionalReconSettings?.enabled) return results;

    const matchedPrKeys = new Set<string>();
    const matchedGstrKeys = new Set<string>();

    const arSettings = settings.additionalReconSettings;
    const normalizedClientGstin = (clientGstin || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const clientPan = normalizedClientGstin.length >= 15 ? normalizedClientGstin.substring(2, 12) : null;

    // PRE-INDEX unmatched 2B for O(1) bucket lookups
    const gstrByGstin = new Map<string, InvoiceGroup[]>();
    const gstrByPan = new Map<string, InvoiceGroup[]>();
    for (const g of unmatchedGstrGroups) {
        const normG = g.aggregated.normGstin;
        const pan = normG.length >= 12 ? normG.substring(2, 12) : 'NOPAN';
        if (!gstrByGstin.has(normG)) gstrByGstin.set(normG, []);
        gstrByGstin.get(normG)!.push(g);
        if (!gstrByPan.has(pan)) gstrByPan.set(pan, []);
        gstrByPan.get(pan)!.push(g);
    }

    for (const prGroup of unmatchedPrGroups) {
        if (matchedPrKeys.has(prGroup.key)) continue;

        const pr = prGroup.aggregated;
        if (!pr.normGstin) continue;
        const prPan = pr.gstin.length >= 12 ? pr.gstin.substring(2, 12) : 'NOPAN';

        // Candidates: Same GSTIN bucket or Same PAN bucket
        const candidates = new Set<InvoiceGroup>();
        (gstrByGstin.get(pr.normGstin) || []).forEach(g => candidates.add(g));
        (gstrByPan.get(prPan) || []).forEach(g => candidates.add(g));

        for (const gstrGroup of candidates) {
            if (matchedGstrKeys.has(gstrGroup.key)) continue;

            const gstr = gstrGroup.aggregated;
            if (!gstr.normGstin) continue;

            // === EARLY CHECK: Self Inter-Branch Detection ===
            // If client PAN matches both PR PAN and 2B PAN, automatically mark as Inter-Branch
            if (settings.panMatch && clientPan &&
                pr.gstin.length >= 15 && gstr.gstin.length >= 15) {
                const prPan = pr.gstin.substring(2, 12);
                const gstrPan = gstr.gstin.substring(2, 12);

                if (clientPan === prPan && clientPan === gstrPan) {
                    const taxDiff = Math.abs(pr.totalCalc - gstr.totalCalc);
                    const dateToleranceUnit = arSettings.date.toleranceUnit || 'DAYS';
                    const dateDiff = getDateDiff(pr.dateObj, gstr.dateObj, dateToleranceUnit);
                    const dateTolerance = dateToleranceUnit === 'MONTHS'
                        ? Math.floor(settings.dateTolerance / 30)
                        : settings.dateTolerance;

                    const isInvMatch = pr.normInvoice === gstr.normInvoice;
                    const isDateMatch = dateDiff <= dateTolerance;
                    const isTaxMatch = checkTaxMatch(taxDiff, gstr.totalCalc, settings);

                    let matchQuality = '';
                    if (isInvMatch && isDateMatch && isTaxMatch) matchQuality = 'Exact Match';
                    else if (isTaxMatch && isDateMatch) matchQuality = 'Matched (Inv No Differs)';
                    else if (isInvMatch) matchQuality = 'Partial Match (Inv Matched)';
                    else matchQuality = 'Partial Match';

                    const remark = `Inter-Branch: ${matchQuality} (Client PAN: ${clientPan})`;

                    results.push(createResult(
                        prGroup, gstrGroup, MatchStatus.INTER_BRANCH,
                        remark,
                        `Tax Diff: ₹${taxDiff.toFixed(2)}, Date Diff: ${dateDiff}${dateToleranceUnit === 'MONTHS' ? 'm' : 'd'}`,
                        taxDiff,
                        activePeriod,
                        dateDiff
                    ));
                    matchedPrKeys.add(prGroup.key);
                    matchedGstrKeys.add(gstrGroup.key);
                    break; // Move to next PR
                }
            }

            // === STANDARD MATCHING LOGIC ===
            const invoiceTypeMode = arSettings.invoiceTypeMatching || 'IGNORE';
            if (!checkInvoiceTypeMatch(pr.type, gstr.type, invoiceTypeMode)) continue;

            let isGstinMatch = false;
            let isInvNoMatch = false;
            let isDateMatch = false;
            let isTaxValueMatch = false;
            let isPanMatch = false;
            let remarkDetails: string[] = [];

            if (arSettings.gstin.exact && pr.normGstin === gstr.normGstin) {
                isGstinMatch = true;
                remarkDetails.push('GSTIN (Exact)');
            } else if (arSettings.gstin.fuzzy && checkGstinFuzzy(pr.normGstin, gstr.normGstin, settings)) {
                isGstinMatch = true;
                remarkDetails.push('GSTIN (Fuzzy)');
            }

            if (arSettings.invoiceNumber.exact && pr.normInvoice === gstr.normInvoice) {
                isInvNoMatch = true;
                remarkDetails.push('Invoice No (Exact)');
            } else if (arSettings.invoiceNumber.fuzzy) {
                const invSim = calculateSimilarity(pr.normInvoice, gstr.normInvoice);
                const invThreshold = (settings.invoiceConfig?.similarityThreshold || 80) / 100;
                if (invSim >= invThreshold) {
                    isInvNoMatch = true;
                    remarkDetails.push(`Invoice No (Fuzzy ${Math.floor(invSim * 100)}%)`);
                }
            }

            const dateToleranceUnit = arSettings.date.toleranceUnit || 'DAYS';
            const dDiff = getDateDiff(pr.dateObj, gstr.dateObj, dateToleranceUnit);
            if (arSettings.date.exact && dDiff === 0) {
                isDateMatch = true;
                remarkDetails.push('Date (Exact)');
            } else if (arSettings.date.tolerance) {
                const tolerance = dateToleranceUnit === 'MONTHS' ? Math.floor(settings.dateTolerance / 30) : settings.dateTolerance;
                if (dDiff <= tolerance) {
                    isDateMatch = true;
                    remarkDetails.push(`Date (Tolerance ${dDiff}${dateToleranceUnit === 'MONTHS' ? 'm' : 'd'})`);
                }
            }

            const taxDiff = Math.abs(pr.totalCalc - gstr.totalCalc);
            if (arSettings.taxValue.exact && taxDiff < 1) {
                isTaxValueMatch = true;
                remarkDetails.push('Tax (Exact)');
            } else if (arSettings.taxValue.tolerance && checkTaxMatch(taxDiff, gstr.totalCalc, settings)) {
                isTaxValueMatch = true;
                remarkDetails.push(`Tax (Tolerance ₹${taxDiff.toFixed(2)})`);
            }

            if (settings.panMatch && pr.gstin.length >= 15 && gstr.gstin.length >= 15) {
                const prPan = pr.gstin.substring(2, 12);
                const gstrPan = gstr.gstin.substring(2, 12);
                if (prPan === gstrPan) {
                    isPanMatch = true;
                    const isSelf = clientPan && prPan === clientPan;
                    remarkDetails.push(isSelf ? 'PAN Match (Inter-branch)' : 'PAN Match (Vendor Branch)');

                    if (!isInvNoMatch) {
                        const invSim = calculateSimilarity(pr.normInvoice, gstr.normInvoice);
                        if (invSim >= 0.7) {
                            isInvNoMatch = true;
                            remarkDetails.push(`Invoice No (Relaxed Match ${(invSim * 100).toFixed(0)}%)`);
                        }
                    }
                }
            }

            let isMatch = false;
            if (isGstinMatch) isMatch = isInvNoMatch || isDateMatch || isTaxValueMatch;
            else if (isPanMatch) isMatch = isInvNoMatch;

            if (isMatch) {
                const remark = `Additional Recon: ${remarkDetails.join(', ')}`;
                let finalStatus = MatchStatus.PARTIALLY_MATCHED;
                const prPan = pr.gstin.substring(2, 12);
                const isSelf = clientPan && prPan === clientPan;

                if (isPanMatch && !isGstinMatch && isSelf) finalStatus = MatchStatus.INTER_BRANCH;

                results.push(createResult(
                    prGroup, gstrGroup, finalStatus,
                    remark, `Diff: ₹${taxDiff.toFixed(2)}`, taxDiff, activePeriod, dDiff
                ));
                matchedPrKeys.add(prGroup.key);
                matchedGstrKeys.add(gstrGroup.key);
                break;
            }
        }
    }
    return results;
};


export const calculateStats = (results: ReconciliationItem[]): Stats => {
    const stats: Stats = {
        matchedCount: 0,
        partiallyMatchedCount: 0,
        unmatchedPRCount: 0,
        unmatched2BCount: 0,
        rcmCount: 0,
        ineligibleItcCount: 0,
        interBranchCount: 0,
        carryForwardCount: 0,
        step1ExactCount: 0,
        step2FuzzyCount: 0,
        total2BInvoiceCount: 0, total2BIgst: 0, total2BCgst: 0, total2BSgst: 0,
        totalPRInvoiceCount: 0, totalPRIgst: 0, totalPRCgst: 0, totalPRSgst: 0,
        interBranchIgst: 0, interBranchCgst: 0, interBranchSgst: 0,
        interBranchPrIgst: 0, interBranchPrCgst: 0, interBranchPrSgst: 0,
        interBranch2bIgst: 0, interBranch2bCgst: 0, interBranch2bSgst: 0,
        matchedPrIgst: 0, matchedPrCgst: 0, matchedPrSgst: 0,
        matched2bIgst: 0, matched2bCgst: 0, matched2bSgst: 0,
        partialPrIgst: 0, partialPrCgst: 0, partialPrSgst: 0,
        partial2bIgst: 0, partial2bCgst: 0, partial2bSgst: 0,
        unmatchedPrIgst: 0, unmatchedPrCgst: 0, unmatchedPrSgst: 0,
        unmatched2bIgst: 0, unmatched2bCgst: 0, unmatched2bSgst: 0,
    };

    results.forEach(r => {
        if (r.status === MatchStatus.MATCHED) stats.matchedCount++;
        if (r.status === MatchStatus.PARTIALLY_MATCHED) stats.partiallyMatchedCount++;
        if (r.status === MatchStatus.UNMATCHED_PR) stats.unmatchedPRCount++;
        if (r.status === MatchStatus.CARRY_FORWARD) {
            stats.unmatchedPRCount++;
            stats.carryForwardCount++;
        }
        if (r.status === MatchStatus.UNMATCHED_2B) stats.unmatched2BCount++;
        if (r.status === MatchStatus.RCM) stats.rcmCount++;
        if (r.status === MatchStatus.INELIGIBLE_ITC) stats.ineligibleItcCount++;
        if (r.status === MatchStatus.INTER_BRANCH) {
            stats.interBranchCount++;
            stats.interBranchIgst += (r.prIgst || 0) + (r.gstrIgst || 0); // Keeping for backward compat if needed
            stats.interBranchCgst += (r.prCgst || 0) + (r.gstrCgst || 0);
            stats.interBranchSgst += (r.prSgst || 0) + (r.gstrSgst || 0);

            stats.interBranchPrIgst += (r.prIgst || 0);
            stats.interBranchPrCgst += (r.prCgst || 0);
            stats.interBranchPrSgst += (r.prSgst || 0);

            stats.interBranch2bIgst += (r.gstrIgst || 0);
            stats.interBranch2bCgst += (r.gstrCgst || 0);
            stats.interBranch2bSgst += (r.gstrSgst || 0);
        }

        // Granular Tax Stats
        if (r.status === MatchStatus.MATCHED) {
            stats.matchedPrIgst += r.prIgst || 0; stats.matchedPrCgst += r.prCgst || 0; stats.matchedPrSgst += r.prSgst || 0;
            stats.matched2bIgst += r.gstrIgst || 0; stats.matched2bCgst += r.gstrCgst || 0; stats.matched2bSgst += r.gstrSgst || 0;
        } else if (r.status === MatchStatus.PARTIALLY_MATCHED) {
            stats.partialPrIgst += r.prIgst || 0; stats.partialPrCgst += r.prCgst || 0; stats.partialPrSgst += r.prSgst || 0;
            stats.partial2bIgst += r.gstrIgst || 0; stats.partial2bCgst += r.gstrCgst || 0; stats.partial2bSgst += r.gstrSgst || 0;
        } else if (r.status === MatchStatus.UNMATCHED_PR || r.status === MatchStatus.CARRY_FORWARD) {
            stats.unmatchedPrIgst += r.prIgst || 0; stats.unmatchedPrCgst += r.prCgst || 0; stats.unmatchedPrSgst += r.prSgst || 0;
        } else if (r.status === MatchStatus.UNMATCHED_2B) {
            stats.unmatched2bIgst += r.gstrIgst || 0; stats.unmatched2bCgst += r.gstrCgst || 0; stats.unmatched2bSgst += r.gstrSgst || 0;
        } else if (r.status === MatchStatus.DUPLICATE) {
            if (r.prInvoiceRef) {
                stats.unmatchedPrIgst += r.prIgst || 0;
                stats.unmatchedPrCgst += r.prCgst || 0;
                stats.unmatchedPrSgst += r.prSgst || 0;
                stats.unmatchedPRCount++;
            } else {
                stats.unmatched2bIgst += r.gstrIgst || 0;
                stats.unmatched2bCgst += r.gstrCgst || 0;
                stats.unmatched2bSgst += r.gstrSgst || 0;
                stats.unmatched2BCount++;
            }
        }

        // --- STEP LOGIC COUNTING ---
        if (r.status === MatchStatus.MATCHED) {
            stats.step1ExactCount++;
        } else if (r.status === MatchStatus.PARTIALLY_MATCHED) {
            if (r.remarks?.includes("Probable Match")) {
                stats.step2FuzzyCount++;
            } else {
                stats.step1ExactCount++;
            }
        }

        if (r.gstrInvoiceRef) {
            const count = r.gstrMatches ? r.gstrMatches.length : 1;
            stats.total2BInvoiceCount += count;
            stats.total2BIgst += r.gstrIgst;
            stats.total2BCgst += r.gstrCgst;
            stats.total2BSgst += r.gstrSgst;
        }

        if (r.prInvoiceRef) {
            const count = r.prMatches ? r.prMatches.length : 1;
            stats.totalPRInvoiceCount += count;
            stats.totalPRIgst += r.prIgst;
            stats.totalPRCgst += r.prCgst;
            stats.totalPRSgst += r.prSgst;
        }
    });

    return stats;
};
